import React from 'react';
import Watermark from './Watermark';

/**
 * Elegant CSS placeholder for product/category imagery.
 * Combines a warm gradient, an ornamental SVG mandala overlay, and a serif label.
 * Optional `sold` prop paints a bold diagonal ribbon so listings remain visible
 * on the site but are unmistakably marked as SOLD.
 */
export const Placeholder = ({ label, ratio = '3/4', tone = 'ivory', className = '', children, image, alt, watermark = true, watermarkSize = 'sm', sold = false }) => {
  const palettes = {
    ivory: 'from-[#F9F6F0] via-[#F6E9E9] to-[#FDFBF7]',
    blush: 'from-[#F6E9E9] via-[#F9F6F0] to-[#F6E9E9]',
    maroon: 'from-[#7C202A] via-[#4A0E17] to-[#7C202A]',
    gold: 'from-[#C5A059]/30 via-[#F9F6F0] to-[#C5A059]/20',
  };
  const isDark = tone === 'maroon';
  const ratioCls =
    ratio === '3/4'
      ? 'aspect-[3/4]'
      : ratio === '1/1'
      ? 'aspect-square'
      : ratio === '16/9'
      ? 'aspect-[16/9]'
      : ratio === '4/5'
      ? 'aspect-[4/5]'
      : ratio === '4/3'
      ? 'aspect-[4/3]'
      : 'aspect-[3/4]';

  if (image) {
    return (
      <div className={`relative overflow-hidden ${ratioCls} ${className}`}>
        <img
          src={image}
          alt={alt || label || 'Trendy Closet'}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {watermark && <Watermark size={watermarkSize} />}
        {sold && (
          <div className="absolute inset-0 pointer-events-none z-20" data-testid="sold-overlay" aria-label="Sold">
            <div className="absolute inset-0 bg-charcoal/25" />
            <div className="absolute top-6 -right-14 w-52 rotate-45 bg-burgundy text-ivory text-center py-2 text-xs uppercase tracking-[0.35em] shadow-lg font-display">
              Sold
            </div>
          </div>
        )}
        {/* Gold frame */}
        <div className="absolute inset-3 border border-[#C5A059]/50 pointer-events-none" />
        {/* Subtle gradient for label legibility (only if label present) */}
        {label && (
          <>
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
            <div className="absolute bottom-5 left-0 right-0 flex flex-col items-center text-center px-4 pointer-events-none">
              <span className="overline text-[#C5A059]">Trendy Closet</span>
              <h4 className="font-display text-2xl md:text-3xl tracking-tight text-white mt-1">{label}</h4>
              <div className="mt-2 h-px w-12 bg-[#C5A059]/80" />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${ratioCls} ${className}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${palettes[tone] || palettes.ivory}`} />
      {/* Mandala SVG */}
      <svg
        className={`absolute inset-0 w-full h-full ${isDark ? 'opacity-[0.12]' : 'opacity-[0.18]'}`}
        viewBox="0 0 400 500"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g
          stroke={isDark ? '#C5A059' : '#7C202A'}
          strokeWidth="0.7"
          fill="none"
          transform="translate(200 250)"
        >
          {[...Array(16)].map((_, i) => (
            <g key={i} transform={`rotate(${(360 / 16) * i})`}>
              <path d="M0 0 C 30 -10, 60 -30, 90 0 C 60 30, 30 10, 0 0 Z" />
              <circle cx="60" cy="0" r="3" />
            </g>
          ))}
          <circle r="120" />
          <circle r="160" strokeDasharray="2 6" />
          <circle r="40" />
        </g>
        {/* Corner paisleys */}
        <g stroke={isDark ? '#C5A059' : '#C5A059'} strokeWidth="0.8" fill="none" opacity="0.7">
          <path d="M30 30 C 60 20, 80 50, 60 80 C 40 70, 35 50, 30 30 Z" />
          <path d="M370 470 C 340 480, 320 450, 340 420 C 360 430, 365 450, 370 470 Z" />
        </g>
      </svg>
      {/* Gold border frame */}
      <div className={`absolute inset-3 border ${isDark ? 'border-[#C5A059]/30' : 'border-[#C5A059]/40'} pointer-events-none`} />
      {/* Label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
        {label && (
          <>
            <span className={`overline mb-3 ${isDark ? 'text-[#C5A059]' : ''}`}>Trendy Closet</span>
            <h4 className={`font-display text-2xl md:text-3xl tracking-tight ${isDark ? 'text-white' : 'text-[#7C202A]'}`}>
              {label}
            </h4>
            <div className={`mt-3 h-px w-12 ${isDark ? 'bg-[#C5A059]/60' : 'bg-[#C5A059]'}`} />
          </>
        )}
        {children}
      </div>
    </div>
  );
};

export default Placeholder;
